import { CompanyInterface } from "./CompanyInterface";

export interface UserInterface {
  id: number;
  created_at: string | Date;
  updated_at: string | Date;
  email: string;
  first_name: string;
  last_name: string;
  roles: RoleInterface[];
  is_editable: boolean;
  certification: Certification | null;
}

export interface RoleInterface {
  id: number;
  created_at: string | Date;
  updated_at: string | Date;
  role_name: string;
  display_name: string;
}
export interface InvitedUserInterface {
  id: number;
  created_at: Date | string;
  updated_at: Date | string;
  invitation_sent: boolean;
  invitation_type: string;
  invited_user: UserInterface;
  invited_by_user: UserInterface;
  invited_for_user: UserInterface | null;
  rater_type: AllRaterType | null;
  item_count: number;
  company_id: number | null;
  company_employee_id: number | null;
  company: CompanyInterface | null;
  days_left: number | null;
  feedback_completion_count: number;
  superadmin_id?: number;
  meta: {
    id: number;
    created_at: Date | string;
    updated_at: Date | string;
    duration_days: string;
    admin_id: number | null;
    feedback_done: boolean;
    comments_deleted: boolean | null;
    year: string;
    feedback_completion_date: Date | string;
    allowed_participants: null | number;
    report_sent: boolean;
  };
  sent_on: Date | string;
}

export interface UserFilterInterface {
  invitationType: string;
  invitationTypeArray: string[];
  all: boolean;
  name: string | null;
  invited_user: number | null;
  invited_for: number | null;
  companyId: number | null;
  adminId: number | null;
  allUsers: boolean | null;
  feedback_completed: boolean;
  year: string | number;
  invitation_sent: boolean;
  recently_completed: boolean;
  feedback: feedbackStatus | null;
  certification: string;
  orderBy: string;
  orderDir: string;
  company_name: string | null;
}

export enum Role {
  Admin = "admin",
  Participants = "participant",
  Raters = "rater",
}

const directReport = "Direct Report";

export enum RaterType {
  Supervisor = "Supervisor",
  DirectReport = directReport,
  Peer = "Peer",
  Other = "Other",
}

export enum DurationDays {
  Three = "3",
  Fifteen = "15",
  Thirty = "30",
}

export enum AllRaterType {
  Self = "Self",
  Supervisor = "Supervisor",
  DirectReport = directReport,
  Peer = "Peer",
  Other = "Other",
}

export const RaterTypeNames: Record<
  RaterType,
  { value: RaterType; display_name: string }
> = {
  [RaterType.Supervisor]: {
    value: RaterType.Supervisor,
    display_name: "Supervisor",
  },
  [RaterType.DirectReport]: {
    value: RaterType.DirectReport,
    display_name: directReport,
  },
  [RaterType.Peer]: {
    value: RaterType.Peer,
    display_name: "Peer",
  },
  [RaterType.Other]: {
    value: RaterType.Other,
    display_name: "Other",
  },
};

export const AllRaterTypeNames: Record<
  AllRaterType,
  { value: AllRaterType; display_name: string }
> = {
  [AllRaterType.Self]: {
    value: AllRaterType.Self,
    display_name: "Self",
  },
  [AllRaterType.Supervisor]: {
    value: AllRaterType.Supervisor,
    display_name: "Supervisor",
  },
  [AllRaterType.DirectReport]: {
    value: AllRaterType.DirectReport,
    display_name: directReport,
  },
  [AllRaterType.Peer]: {
    value: AllRaterType.Peer,
    display_name: "Peer",
  },
  [AllRaterType.Other]: {
    value: AllRaterType.Other,
    display_name: "Other",
  },
};

export interface InviteUserInterface {
  first_name: string;
  last_name: string;
  email: string;
  invited_for?: null | number;
  invitation_type: string;
  rater_type?: null | string;
  duration_days?: string | null;
  feedback_completion_date?: string | null;
  admin_id?: number | null;
  allowed_participants?: number | null;
  superadmin_id?: number;
}

export interface DashboardInterface {
  super_admin: {
    total_companies: {
      title: string;
      data: number;
    };
    total_users: {
      title: string;
      data: number;
    };
    total_active_sessions: {
      title: string;
      data: number;
    };
    last_week_feedbacks: {
      title: string;
      data: 0;
    };
  };
  admin: {
    total_companies: {
      title: string;
      data: number;
    };
    total_users: {
      title: string;
      data: number;
    };
    total_active_sessions: {
      title: string;
      data: number;
    };
    last_week_feedbacks: {
      title: string;
      data: 0;
    };
  };
  participant: {
    days_left: {
      title: string;
      data: number;
    };
    feedback_completion_count: {
      title: string;
      data: number;
    };
    supervisor: {
      title: string;
      data: number;
    };
    self: {
      title: string;
      data: number;
    };
    direct_report: {
      title: string;
      data: number;
    };
    peer: {
      title: string;
      data: number;
    };
    other: {
      title: string;
      data: number;
    };
  };
  rater: {
    total_feedbacks: {
      title: string;
      data: number;
    };
    completed_feedbacks: {
      title: string;
      data: number;
    };
  };
}
interface AssessmentField {
  title: string;
  data: number | string;
}

export interface ParticipantDashboard {
  raters_invited: AssessmentField;
  total_assessments: AssessmentField;
  completed_feedbacks: AssessmentField;
  days_left: AssessmentField;
  feedback_completion_count: AssessmentField;
  supervisor: AssessmentField;
  self: AssessmentField;
  direct_report: AssessmentField;
  peer: AssessmentField;
  other: AssessmentField;
}
export interface ParticipantInterface {
  days_left: number;
  feedback_completion_count: number;
}

export enum Roles {
  SuperAdmin = "super_admin",
  Admin = "admin",
  Participant = "participant",
  Rater = "rater",
}

export interface Certification {
  id: number;
  user_id: number;
  certified_on: string;
}

export enum feedbackStatus {
  Ongoing = "ongoing",
  Completed = "completed",
}

export enum CertificationStatus {
  Certified = "certified",
  NonCertified = "non-certified",
}
